import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ThumbsUp, Eye, Plus, MessageSquare, Send, Reply, Lightbulb, Bug, Zap, MessageCircle } from 'lucide-react';

// Use window.location.origin so nginx proxy handles backend routing
const BACKEND_URL = window.location.origin;

export const PublicFeedback = () => {
  const { tenantSlug } = useParams();
  const { t } = useLanguage();
  const [tenant, setTenant] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [projectName, setProjectName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [sortBy, setSortBy] = useState('trending');
  const [filterStatus, setFilterStatus] = useState('all');
  const [votedFeedbacks, setVotedFeedbacks] = useState(new Set());
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [comments, setComments] = useState({}); // feedback_id -> comments array
  const [loadingComments, setLoadingComments] = useState({}); // feedback_id -> loading state
  const [newComment, setNewComment] = useState({}); // feedback_id -> comment data
  const [replyingTo, setReplyingTo] = useState({}); // feedback_id -> parent_comment_id
  const [replyContent, setReplyContent] = useState({}); // feedback_id -> { parent_comment_id: content }
  const [newFeedback, setNewFeedback] = useState({
    title: '',
    description: '',
    type: 'feature',
    submitter_name: '',
    submitter_email: ''
  });

  const STATUS_LABELS = {
    backlog: t.backlog,
    planned: t.planned,
    in_development: t.inDevelopment,
    testing: t.testing,
    completed: t.completed,
    new: t.new,
    under_review: t.underReview,
    in_progress: t.inProgress
  };

  useEffect(() => {
    if (tenantSlug) {
      fetchFeedbackData();
      fetchProjectName();
      // Load voted feedbacks from localStorage
      const savedVotes = JSON.parse(localStorage.getItem(`voted_feedbacks_${tenantSlug}`) || '[]');
      setVotedFeedbacks(new Set(savedVotes));
      
      // Load saved email and name for comments
      const savedEmail = localStorage.getItem(`feedbackflow_email_${tenantSlug}`);
      const savedName = localStorage.getItem(`feedbackflow_name_${tenantSlug}`);
      if (savedEmail || savedName) {
        // Initialize newComment with saved data for all feedbacks
        setNewComment(prev => ({
          ...prev,
          commenter_email: savedEmail || '',
          commenter_name: savedName || ''
        }));
      }
    }
  }, [tenantSlug]);

  const fetchProjectName = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tenants/lookup/${tenantSlug}`);
      setProjectName(response.data.name);
    } catch (error) {
      console.error('Failed to fetch project name:', error);
    }
  };

  const fetchFeedbackData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/feedback/${tenantSlug}`);

      // Ensure response.data.tenant exists
      if (!response.data || !response.data.tenant) {
        throw new Error('Invalid response format from server');
      }

      setTenant(response.data.tenant);
      setFeedbacks(response.data.feedbacks || []);
      // Check if comments are enabled
      if (!response.data.tenant.comments_enabled) {
        // Close all comment sections if comments are disabled
        setExpandedComments(new Set());
      }
      
      // Increment views for each feedback (only once per session)
      const viewedFeedbacks = JSON.parse(localStorage.getItem(`viewed_feedbacks_${tenantSlug}`) || '[]');
      const feedbacksToIncrement = response.data.feedbacks.filter(
        f => !viewedFeedbacks.includes(f.id)
      );
      
      if (feedbacksToIncrement.length > 0) {
        // Mark as viewed
        const newViewedFeedbacks = [...viewedFeedbacks, ...feedbacksToIncrement.map(f => f.id)];
        localStorage.setItem(`viewed_feedbacks_${tenantSlug}`, JSON.stringify(newViewedFeedbacks));
        
        // Increment views in backend
        feedbacksToIncrement.forEach(async (feedback) => {
          try {
            const response = await axios.post(`${BACKEND_URL}/api/public/feedbacks/${feedback.id}/increment-view`);
            // Update the feedback in the list with new views count
            setFeedbacks(prevFeedbacks => 
              prevFeedbacks.map(f => 
                f.id === feedback.id ? { ...f, views: response.data.views } : f
              )
            );
          } catch (error) {
            console.error(`Failed to increment view for feedback ${feedback.id}:`, error);
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      if (error.response?.status === 404) {
        toast.error(t.projectNotFound);
      } else {
        toast.error(t.failedToLoadFeedback);
      }
      setTenant(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tenant) {
      toast.error(t.projectNotFound);
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/public/feedback/${tenantSlug}`, newFeedback);
      
      // Save email and name to localStorage for future votes
      if (newFeedback.submitter_email) {
        localStorage.setItem(`feedbackflow_email_${tenantSlug}`, newFeedback.submitter_email);
        if (newFeedback.submitter_name) {
          localStorage.setItem(`feedbackflow_name_${tenantSlug}`, newFeedback.submitter_name);
        }
      }
      
      toast.success(t.feedbackSubmittedSuccess);
      setShowDialog(false);
      setNewFeedback({ title: '', description: '', type: 'feature', submitter_name: '', submitter_email: '' });
      fetchFeedbackData();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(t.projectNotFound);
      } else {
        toast.error(error.response?.data?.detail || t.failedToLoadFeedback);
      }
    }
  };

  const toggleComments = (feedbackId) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(feedbackId)) {
      newExpanded.delete(feedbackId);
    } else {
      newExpanded.add(feedbackId);
      // Load comments if not already loaded
      if (!comments[feedbackId]) {
        fetchComments(feedbackId);
      }
    }
    setExpandedComments(newExpanded);
  };

  const fetchComments = async (feedbackId) => {
    setLoadingComments(prev => ({ ...prev, [feedbackId]: true }));
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/feedbacks/${feedbackId}/comments`);
      setComments(prev => ({ ...prev, [feedbackId]: response.data }));
    } catch (error) {
      console.error(`Failed to fetch comments for feedback ${feedbackId}:`, error);
      toast.error(t.failedToLoadComments || 'Yorumlar yüklenemedi');
    } finally {
      setLoadingComments(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handleSubmitComment = async (feedbackId, e) => {
    e.preventDefault();
    const comment = newComment[feedbackId] || {};
    if (!comment.content || !comment.content.trim()) {
      toast.error(t.commentRequired || 'Yorum boş olamaz');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/public/feedbacks/${feedbackId}/comments`,
        {
          content: comment.content,
          commenter_name: comment.commenter_name || '',
          commenter_email: comment.commenter_email || ''
        }
      );

      // Save email and name to localStorage for future comments
      if (comment.commenter_email) {
        localStorage.setItem(`feedbackflow_email_${tenantSlug}`, comment.commenter_email);
        if (comment.commenter_name) {
          localStorage.setItem(`feedbackflow_name_${tenantSlug}`, comment.commenter_name);
        }
      }

      // Add new comment to the list
      setComments(prev => ({
        ...prev,
        [feedbackId]: [...(prev[feedbackId] || []), response.data]
      }));

      // Clear comment input
      setNewComment(prev => ({
        ...prev,
        [feedbackId]: { ...prev[feedbackId], content: '' }
      }));

      // Update feedback comment count
      setFeedbacks(prevFeedbacks =>
        prevFeedbacks.map(f =>
          f.id === feedbackId
            ? { ...f, comments_count: (f.comments_count || 0) + 1 }
            : f
        )
      );

      toast.success(t.commentSubmitted || 'Yorum gönderildi');
    } catch (error) {
      console.error(`Failed to submit comment:`, error);
      toast.error(error.response?.data?.detail || t.failedToSubmitComment || 'Yorum gönderilemedi');
    }
  };

  const handleSubmitReply = async (feedbackId, parentCommentId, e) => {
    e.preventDefault();
    const reply = replyContent[feedbackId]?.[parentCommentId] || {};
    if (!reply.content || !reply.content.trim()) {
      toast.error(t.commentRequired || 'Cevap boş olamaz');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/public/feedbacks/${feedbackId}/comments`,
        {
          content: reply.content,
          commenter_name: reply.commenter_name || '',
          commenter_email: reply.commenter_email || '',
          parent_comment_id: parentCommentId
        }
      );

      // Save email and name to localStorage for future replies
      if (reply.commenter_email) {
        localStorage.setItem(`feedbackflow_email_${tenantSlug}`, reply.commenter_email);
        if (reply.commenter_name) {
          localStorage.setItem(`feedbackflow_name_${tenantSlug}`, reply.commenter_name);
        }
      }

      // Add new reply to the list
      setComments(prev => ({
        ...prev,
        [feedbackId]: [...(prev[feedbackId] || []), response.data]
      }));

      // Clear reply input
      setReplyContent(prev => ({
        ...prev,
        [feedbackId]: {
          ...(prev[feedbackId] || {}),
          [parentCommentId]: { content: '', commenter_name: '', commenter_email: '' }
        }
      }));

      // Close reply form
      setReplyingTo(prev => ({
        ...prev,
        [feedbackId]: prev[feedbackId] === parentCommentId ? null : prev[feedbackId]
      }));

      // Update feedback comment count
      setFeedbacks(prevFeedbacks =>
        prevFeedbacks.map(f =>
          f.id === feedbackId
            ? { ...f, comments_count: (f.comments_count || 0) + 1 }
            : f
        )
      );

      toast.success(t.replySubmitted || 'Cevap gönderildi');
    } catch (error) {
      console.error(`Failed to submit reply:`, error);
      toast.error(error.response?.data?.detail || t.replySubmitFailed || 'Cevap gönderilemedi');
    }
  };

  // Organize comments into hierarchical structure
  const organizeComments = (commentsList) => {
    if (!commentsList) return { topLevel: [], replies: {} };
    
    const topLevel = [];
    const replies = {};
    
    commentsList.forEach(comment => {
      if (comment.parent_comment_id) {
        if (!replies[comment.parent_comment_id]) {
          replies[comment.parent_comment_id] = [];
        }
        replies[comment.parent_comment_id].push(comment);
      } else {
        topLevel.push(comment);
      }
    });
    
    // Sort top level and replies by created_at
    topLevel.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    Object.keys(replies).forEach(parentId => {
      replies[parentId].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });
    
    return { topLevel, replies };
  };

  const handleVote = async (feedbackId) => {
    try {
      // Check if already voted
      const isVoted = votedFeedbacks.has(feedbackId);
      
      // Try to get email from localStorage (if user submitted feedback before)
      const savedEmail = localStorage.getItem(`feedbackflow_email_${tenantSlug}`);
      const savedName = localStorage.getItem(`feedbackflow_name_${tenantSlug}`);
      
      const payload = {
        toggle: true,  // Indicate this is a toggle action
        action: isVoted ? 'unvote' : 'vote'  // Tell backend if we're voting or unvoting
      };
      if (savedEmail) {
        payload.voter_email = savedEmail;
        if (savedName) {
          payload.voter_name = savedName;
        }
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/public/feedbacks/${feedbackId}/vote`, payload);
      
      // Update voted feedbacks set
      const newVotedFeedbacks = new Set(votedFeedbacks);
      if (isVoted) {
        newVotedFeedbacks.delete(feedbackId);
        toast.success(t.voteRemoved);
      } else {
        newVotedFeedbacks.add(feedbackId);
        toast.success(t.voteSaved);
      }
      setVotedFeedbacks(newVotedFeedbacks);
      
      // Save to localStorage
      localStorage.setItem(`voted_feedbacks_${tenantSlug}`, JSON.stringify(Array.from(newVotedFeedbacks)));
      
      // Update feedback in the list
      setFeedbacks(prevFeedbacks => 
        prevFeedbacks.map(f => 
          f.id === feedbackId ? { ...f, upvotes: response.data.upvotes } : f
        )
      );
    } catch (error) {
      toast.error(t.failedToVote);
    }
  };

  const getSortedFeedbacks = () => {
    let filtered = feedbacks;
    
    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(f => f.status === filterStatus);
    }
    
    // Sort
    return filtered.sort((a, b) => {
      if (sortBy === 'trending') return b.upvotes - a.upvotes;
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-900 dark:text-white">{t.loading}</div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <PublicHeader
          title={t.projectNotFound}
          description={t.projectNotFoundDescription}
          projectName={projectName}
          tenantSlug={tenantSlug}
        />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <Card className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t.projectNotFound}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t.projectNotFoundText}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t.contactAdministrator}
            </p>
          </Card>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>{projectName || tenant?.name || tenantSlug || 'Project'} - Feedback | FeedPanels</title>
        <meta name="description" content={`View and submit feedback for ${projectName || tenant?.name || 'this project'}. Share your ideas and help improve the product.`} />
        <link rel="canonical" href={`https://feedpanels.com/feedback/${tenantSlug}`} />
      </Helmet>
      <PublicHeader
        title={t.feedbackTitle}
        description={t.feedbackDescription}
        projectName={projectName || tenant.name}
        tenantSlug={tenantSlug}
      >
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button size="lg" className="text-xs md:text-base" data-testid="public-submit-feedback-button">
              <Plus className="w-4 md:w-5 h-4 md:h-5 mr-1 md:mr-2" />
              {t.submitFeedback}
            </Button>
          </DialogTrigger>
          <DialogContent className="w-full max-w-2xl mx-4">
            <DialogHeader>
              <DialogTitle className="text-lg md:text-xl">{t.submitYourFeedback}</DialogTitle>
              <DialogDescription className="text-xs md:text-sm">
                {t.helpUsImprove}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              <div>
                <label className="text-xs md:text-sm font-medium">{t.feedbackType} *</label>
                <Select value={newFeedback.type} onValueChange={(value) => setNewFeedback({ ...newFeedback, type: value })}>
                  <SelectTrigger data-testid="public-feedback-type" className="text-xs md:text-sm">
                    <SelectValue>
                      {newFeedback.type === 'feature' && (
                        <div className="flex items-center">
                          <Lightbulb className="w-4 h-4 mr-2 text-amber-500" />
                          {t.featureRequest}
                        </div>
                      )}
                      {newFeedback.type === 'bug' && (
                        <div className="flex items-center">
                          <Bug className="w-4 h-4 mr-2 text-red-500" />
                          {t.bugReport}
                        </div>
                      )}
                      {newFeedback.type === 'improvement' && (
                        <div className="flex items-center">
                          <Zap className="w-4 h-4 mr-2 text-blue-500" />
                          {t.improvement}
                        </div>
                      )}
                      {newFeedback.type === 'other' && (
                        <div className="flex items-center">
                          <MessageCircle className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                          {t.other}
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">
                      <div className="flex items-center">
                        <Lightbulb className="w-4 h-4 mr-2 text-amber-500" />
                        {t.featureRequest}
                      </div>
                    </SelectItem>
                    <SelectItem value="bug">
                      <div className="flex items-center">
                        <Bug className="w-4 h-4 mr-2 text-red-500" />
                        {t.bugReport}
                      </div>
                    </SelectItem>
                    <SelectItem value="improvement">
                      <div className="flex items-center">
                        <Zap className="w-4 h-4 mr-2 text-blue-500" />
                        {t.improvement}
                      </div>
                    </SelectItem>
                    <SelectItem value="other">
                      <div className="flex items-center">
                        <MessageCircle className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                        {t.other}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs md:text-sm font-medium">{t.title} *</label>
                <Input
                  value={newFeedback.title}
                  onChange={(e) => setNewFeedback({ ...newFeedback, title: e.target.value })}
                  placeholder={t.briefDescription}
                  required
                  data-testid="public-feedback-title"
                  className="text-xs md:text-sm"
                />
              </div>
              <div>
                <label className="text-xs md:text-sm font-medium">{t.description} *</label>
                <Textarea
                  value={newFeedback.description}
                  onChange={(e) => setNewFeedback({ ...newFeedback, description: e.target.value })}
                  placeholder={t.provideMoreDetails}
                  required
                  rows={5}
                  data-testid="public-feedback-description"
                  className="text-xs md:text-sm"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label className="text-xs md:text-sm font-medium">{t.yourName}</label>
                  <Input
                    value={newFeedback.submitter_name}
                    onChange={(e) => setNewFeedback({ ...newFeedback, submitter_name: e.target.value })}
                    placeholder={t.yourName}
                    data-testid="public-feedback-name"
                    className="text-xs md:text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium">{t.yourEmail} *</label>
                  <Input
                    type="email"
                    value={newFeedback.submitter_email}
                    onChange={(e) => setNewFeedback({ ...newFeedback, submitter_email: e.target.value })}
                    placeholder={t.yourEmail}
                    required
                    data-testid="public-feedback-email"
                    className="text-xs md:text-sm"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full text-xs md:text-sm" size="lg" data-testid="public-feedback-submit">
                {t.submitFeedback}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PublicHeader>

      <div className="max-w-6xl mx-auto px-3 md:px-6 py-6 md:py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0 mb-6">
          <Tabs value={filterStatus} onValueChange={setFilterStatus} className="w-full md:w-auto">
            <TabsList className="grid grid-cols-3 md:flex w-full md:w-auto">
              <TabsTrigger value="all" data-testid="filter-all" className="text-xs md:text-sm">{t.all}</TabsTrigger>
              <TabsTrigger value="planned" data-testid="filter-planned" className="text-xs md:text-sm">{t.planned}</TabsTrigger>
              <TabsTrigger value="in_development" data-testid="filter-in-development" className="text-xs md:text-sm">{t.inDevelopment}</TabsTrigger>
            </TabsList>
          </Tabs>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full md:w-48 text-xs md:text-sm" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="trending" className="text-xs md:text-sm">{t.mostVoted}</SelectItem>
              <SelectItem value="newest" className="text-xs md:text-sm">{t.newestFirst}</SelectItem>
              <SelectItem value="oldest" className="text-xs md:text-sm">{t.oldestFirst}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Feedback List */}
        {getSortedFeedbacks().length === 0 ? (
          <Card className="p-8 md:p-12 text-center">
            <p className="text-sm md:text-base text-gray-500 mb-2 md:mb-4">{t.noFeedbackYet}</p>
            <p className="text-xs md:text-sm text-gray-400">{t.beFirstToShare}</p>
          </Card>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {getSortedFeedbacks().map((feedback) => (
              <Card
                key={feedback.id}
                className="feedback-card"
                data-testid={`public-feedback-${feedback.id}`}
              >
                <CardContent className="p-3 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                    {/* Vote Button */}
                    <button
                      onClick={() => handleVote(feedback.id)}
                      className="flex flex-col items-center space-y-1 group flex-shrink-0"
                      data-testid={`public-vote-${feedback.id}`}
                    >
                      <div className={`w-10 md:w-12 h-10 md:h-12 rounded-lg md:rounded-xl border-2 flex items-center justify-center transition-all ${
                        votedFeedbacks.has(feedback.id)
                          ? 'border-cyan-600 bg-cyan-50'
                          : 'border-gray-300 group-hover:border-cyan-600 group-hover:bg-cyan-50'
                      }`}>
                        <ThumbsUp className={`w-4 md:w-5 h-4 md:h-5 transition-colors ${
                          votedFeedbacks.has(feedback.id)
                            ? 'text-cyan-600'
                            : 'text-gray-600 group-hover:text-cyan-600'
                        }`} />
                      </div>
                      <span className="text-xs md:text-sm font-bold text-gray-700 dark:text-gray-300">{feedback.upvotes || 0}</span>
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-0 mb-2">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          {feedback.type === 'bug' && <Bug className="w-4 md:w-5 h-4 md:h-5 text-red-500 flex-shrink-0" />}
                          {feedback.type === 'feature' && <Lightbulb className="w-4 md:w-5 h-4 md:h-5 text-amber-500 flex-shrink-0" />}
                          {feedback.type === 'improvement' && <Zap className="w-4 md:w-5 h-4 md:h-5 text-blue-500 flex-shrink-0" />}
                          {feedback.type === 'other' && <MessageCircle className="w-4 md:w-5 h-4 md:h-5 text-gray-500 flex-shrink-0" />}
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white truncate">{feedback.title}</h3>
                        </div>
                        <Badge className={`status-${feedback.status} text-xs md:text-sm flex-shrink-0`}>
                          {STATUS_LABELS[feedback.status] || feedback.status}
                        </Badge>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600 dark:text-gray-200 mb-2 md:mb-4">{feedback.description}</p>
                      <div className="flex flex-wrap items-center gap-2 md:gap-6 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                        {feedback.type && (
                          <Badge variant="outline" className="text-[10px] md:text-xs">
                            {feedback.type === 'feature' && t.featureRequest}
                            {feedback.type === 'bug' && t.bugReport}
                            {feedback.type === 'improvement' && t.improvement}
                            {feedback.type === 'other' && t.other}
                          </Badge>
                        )}
                        <div className="flex items-center space-x-1">
                          <Eye className="w-3 md:w-4 h-3 md:h-4" />
                          <span>{feedback.views || 0} {t.views}</span>
                        </div>
                        {feedback.submitter_name && (
                          <span>{t.by} {feedback.submitter_name}</span>
                        )}
                        {tenant?.comments_enabled !== false && (
                          <button
                            onClick={() => toggleComments(feedback.id)}
                            className="flex items-center space-x-1 text-gray-500 hover:text-cyan-600 transition-colors"
                            data-testid={`public-comment-toggle-${feedback.id}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                            <span>{feedback.comments_count || 0} {t.comments || 'Yorumlar'}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Comments Section */}
                  {tenant?.comments_enabled !== false && expandedComments.has(feedback.id) && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-6">
                        <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                          <MessageSquare className="w-5 h-5 text-cyan-600" />
                          <span>{t.comments || 'Yorumlar'} ({comments[feedback.id]?.length || 0})</span>
                        </h4>
                      </div>
                      
                      {/* Comments List */}
                      {loadingComments[feedback.id] ? (
                        <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">{t.loading || 'Yükleniyor...'}</div>
                      ) : (
                        <div className="space-y-4 mb-6">
                          {(() => {
                            const { topLevel, replies } = organizeComments(comments[feedback.id]);
                            if (topLevel.length === 0) {
                              return (
                                <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center bg-gray-50 dark:bg-gray-800 rounded-lg">
                                  {t.noCommentsYet || 'Henüz yorum yok. İlk yorumu siz yapın!'}
                                </div>
                              );
                            }
                            return topLevel.map((comment) => (
                              <div key={comment.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm hover:shadow-md dark:hover:shadow-lg transition-shadow">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                                      {(comment.user_name || comment.commenter_name || 'A').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                          {comment.user_name || comment.commenter_name || 'Anonim'}
                                        </span>
                                      </div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {new Date(comment.created_at).toLocaleDateString('tr-TR', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-gray-700 dark:text-gray-200 text-sm whitespace-pre-wrap mb-4 leading-relaxed">{comment.content}</p>

                                {/* Reply Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const savedEmail = localStorage.getItem(`feedbackflow_email_${tenantSlug}`);
                                    const savedName = localStorage.getItem(`feedbackflow_name_${tenantSlug}`);
                                    setReplyingTo(prev => ({
                                      ...prev,
                                      [feedback.id]: prev[feedback.id] === comment.id ? null : comment.id
                                    }));
                                    setReplyContent(prev => ({
                                      ...prev,
                                      [feedback.id]: {
                                        ...(prev[feedback.id] || {}),
                                        [comment.id]: {
                                          content: '',
                                          commenter_name: savedName || '',
                                          commenter_email: savedEmail || ''
                                        }
                                      }
                                    }));
                                  }}
                                  className="text-xs text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 dark:text-cyan-400 dark:hover:text-cyan-300 dark:hover:bg-cyan-900/20"
                                >
                                  <Reply className="w-3 h-3 mr-1" />
                                  {replyingTo[feedback.id] === comment.id ? (t.cancelReply || 'İptal') : (t.reply || 'Cevap Ver')}
                                </Button>

                                {/* Reply Form */}
                                {replyingTo[feedback.id] === comment.id && (
                                  <form onSubmit={(e) => handleSubmitReply(feedback.id, comment.id, e)} className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3">
                                    <Textarea
                                      value={replyContent[feedback.id]?.[comment.id]?.content || ''}
                                      onChange={(e) => setReplyContent(prev => ({
                                        ...prev,
                                        [feedback.id]: {
                                          ...(prev[feedback.id] || {}),
                                          [comment.id]: {
                                            ...(prev[feedback.id]?.[comment.id] || {}),
                                            content: e.target.value
                                          }
                                        }
                                      }))}
                                      placeholder={t.writeReply || 'Cevabınızı yazın...'}
                                      rows={2}
                                      className="resize-none text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                                    />
                                    <div className="grid md:grid-cols-2 gap-2">
                                      <Input
                                        value={replyContent[feedback.id]?.[comment.id]?.commenter_name || ''}
                                        onChange={(e) => setReplyContent(prev => ({
                                          ...prev,
                                          [feedback.id]: {
                                            ...(prev[feedback.id] || {}),
                                            [comment.id]: {
                                              ...(prev[feedback.id]?.[comment.id] || {}),
                                              commenter_name: e.target.value
                                            }
                                          }
                                        }))}
                                        placeholder={t.yourName || 'Adınız'}
                                        className="text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                                      />
                                      <Input
                                        type="email"
                                        value={replyContent[feedback.id]?.[comment.id]?.commenter_email || ''}
                                        onChange={(e) => setReplyContent(prev => ({
                                          ...prev,
                                          [feedback.id]: {
                                            ...(prev[feedback.id] || {}),
                                            [comment.id]: {
                                              ...(prev[feedback.id]?.[comment.id] || {}),
                                              commenter_email: e.target.value
                                            }
                                          }
                                        }))}
                                        placeholder={t.yourEmail || 'E-posta'}
                                        className="text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                                      />
                                    </div>
                                    <div className="flex space-x-2">
                                      <Button type="submit" size="sm" className="text-xs">
                                        <Send className="w-3 h-3 mr-1" />
                                        {t.sendReply || 'Gönder'}
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setReplyingTo(prev => ({
                                            ...prev,
                                            [feedback.id]: null
                                          }));
                                        }}
                                        className="text-xs"
                                      >
                                        {t.cancel || 'İptal'}
                                      </Button>
                                    </div>
                                  </form>
                                )}

                                {/* Replies */}
                                {replies[comment.id] && replies[comment.id].length > 0 && (
                                  <div className="mt-4 ml-6 pl-4 border-l-2 border-cyan-200 dark:border-cyan-700 space-y-3">
                                    {replies[comment.id].map((reply) => (
                                      <div key={reply.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        <div className="flex items-start justify-between mb-2">
                                          <div className="flex items-center space-x-2">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white font-semibold text-xs">
                                              {(reply.user_name || reply.commenter_name || 'A').charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                              <span className="font-semibold text-gray-900 dark:text-white text-sm">
                                                {reply.user_name || reply.commenter_name || 'Anonim'}
                                              </span>
                                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                                {new Date(reply.created_at).toLocaleDateString('tr-TR', {
                                                  year: 'numeric',
                                                  month: 'short',
                                                  day: 'numeric',
                                                  hour: '2-digit',
                                                  minute: '2-digit'
                                                })}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                        <p className="text-gray-700 dark:text-gray-200 text-sm whitespace-pre-wrap ml-10">{reply.content}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ));
                          })()}
                        </div>
                      )}

                      {/* Comment Form - Only show if comments are enabled */}
                      {tenant?.comments_enabled !== false && (
                      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl p-5 border border-cyan-200 dark:border-cyan-800">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t.writeComment || 'Yorum Yap'}</h5>
                        <form onSubmit={(e) => handleSubmitComment(feedback.id, e)} className="space-y-3">
                          <Textarea
                            value={newComment[feedback.id]?.content || ''}
                            onChange={(e) => setNewComment(prev => ({
                              ...prev,
                              [feedback.id]: {
                                ...prev[feedback.id],
                                content: e.target.value,
                                commenter_name: prev[feedback.id]?.commenter_name || newComment.commenter_name || '',
                                commenter_email: prev[feedback.id]?.commenter_email || newComment.commenter_email || ''
                              }
                            }))}
                            placeholder={t.writeComment || 'Yorumunuzu yazın...'}
                            rows={4}
                            className="resize-none bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            data-testid={`public-comment-input-${feedback.id}`}
                          />
                          <div className="grid md:grid-cols-2 gap-3">
                            <Input
                              value={newComment[feedback.id]?.commenter_name || newComment.commenter_name || ''}
                              onChange={(e) => {
                                const name = e.target.value;
                                setNewComment(prev => ({
                                  ...prev,
                                  commenter_name: name,
                                  [feedback.id]: {
                                    ...prev[feedback.id],
                                    commenter_name: name
                                  }
                                }));
                              }}
                              placeholder={t.yourName || 'Adınız'}
                              className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              data-testid={`public-comment-name-${feedback.id}`}
                            />
                            <Input
                              type="email"
                              value={newComment[feedback.id]?.commenter_email || newComment.commenter_email || ''}
                              onChange={(e) => {
                                const email = e.target.value;
                                setNewComment(prev => ({
                                  ...prev,
                                  commenter_email: email,
                                  [feedback.id]: {
                                    ...prev[feedback.id],
                                    commenter_email: email
                                  }
                                }));
                              }}
                              placeholder={t.yourEmail || 'E-posta adresiniz'}
                              className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                              data-testid={`public-comment-email-${feedback.id}`}
                            />
                          </div>
                          <Button 
                            type="submit" 
                            size="sm" 
                            className="w-full md:w-auto bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                            data-testid={`public-comment-submit-${feedback.id}`}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {t.submitComment || 'Yorum Gönder'}
                          </Button>
                        </form>
                      </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
};

